/**
 * Générateur — Formuler des conjectures en s'appuyant sur un langage
 * algorithmique ou un tableur.
 * Attendu ID : 940 | Niveau : 5ème | Domaine : Nombres et calculs / Calcul littéral
 *
 * Plan de cartes (mise en conformité avec la spécification 940, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Conjecturer avec un tableur » (id tableur) — niveaux 2 et 3
 *     tableur_formule_texte : la SAISIE d'une formule de colonne (premier
 *       temps du type_reponse), convention d'écriture annoncée.
 *     tableur_formule_qcm_unique : la même formule en réponses fermées.
 *     tableur_texte_trou_saisie : deux colonnes à remplir sur cinq valeurs.
 *     tableur_qcm_unique : la conjecture, quantifiée.
 *
 *   Carte « Une formule à étirer » (id etirer) — niveaux 1 et 2
 *     etirer_texte_trou_saisie + etirer_qcm_unique + etirer_formule_qcm_unique.
 *
 *   Carte « Quand la régularité se casse » (id defaut) — niveau 4
 *     defaut_qcm_unique : le contre-exemple est TOUJOURS dans les lignes
 *       affichées ; la bonne conjecture est restreinte et nomme le rang.
 *
 *   Carte « Encadrer la solution par balayage » (id balayage) — niveau 5
 *     balayage_texte_trou_saisie + balayage_qcm_unique : encadrement à
 *       l'unité, au dixième ou au centième, jamais une valeur arrondie.
 *
 *   Carte « Conjecturer avec un algorithme » (id pseudocode) — niveau 6
 *     pseudocode_texte_trou_saisie + pseudocode_qcm_unique : pseudo-code
 *       FRANÇAIS (affectation, boucle, test), aucun logiciel nommé.
 *
 *   Carte « Suite de motifs et tableau » (id motifs) — variante transverse
 *     motifs_qcm_unique : les premiers motifs sont donnés, comptés dans un
 *       tableau, puis la règle générale est conjecturée.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\940.json) :
 *   Bornes appliquées : deux outils et deux seulement, le tableur et un
 *   langage algorithmique (borne `prescrit`) ; 5 à 20 cas explorés ; valeur de
 *   départ −10..10 ; pas de 1 (sauf le balayage, qui utilise la précision) ;
 *   précision d'encadrement dans la liste fermée 1 / 0,1 / 0,01 ; coefficients
 *   1..10 ; 1 à 3 colonnes construites ; une situation sur trois prend la
 *   régularité en défaut, et le contre-exemple est toujours affiché.
 *   Exclusions appliquées : aucune démonstration demandée ; aucun autre outil ;
 *   aucun logiciel nommé ni copie d'interface ; aucune fonction de tableur
 *   au-delà des quatre opérations et de la recopie ; aucun langage réel ;
 *   aucun arrondi servant à trancher une égalité ; aucune régularité qui ne se
 *   verrait qu'après vingt lignes ; aucune convention de séparateur logicielle.
 *   Erreurs visées → mots-clés :
 *     conclure d'un seul cas                 → un_seul_cas
 *     résultat affiché pris pour la formule  → resultat_pris_pour_formule
 *     valeurs écrites au lieu des références → valeurs_au_lieu_de_references
 *     référence relative et absolue confondues → reference_relative_ou_absolue
 *     conjecture énoncée pour la valeur testée → conjecture_pour_une_valeur
 *     quantification omise                   → quantification_omise
 *     valeur approchée prise pour exacte     → approche_pris_pour_exact
 *     contre-exemple affiché ignoré          → contre_exemple_ignore
 *     encadrement incomplet ou trop large    → encadrement_incomplet
 *     initialisation oubliée dans la boucle  → initialisation_oubliee
 *   Graduation → cartes : 1 → etirer_qcm_unique, motifs_qcm_unique ;
 *   2 → tableur_formule_*, etirer_formule_qcm_unique, etirer_texte_trou_saisie ;
 *   3 → tableur_texte_trou_saisie, tableur_qcm_unique ; 4 → defaut_qcm_unique ;
 *   5 → balayage_* ; 6 → pseudocode_* ; variante transverse → motifs_qcm_unique.
 *   Non tranché : le `type_reponse` veut les deux temps (l'outil PUIS la
 *   conjecture) dans une même question évaluée ensemble ; aucune fabrique du
 *   dépôt ne le permet, les deux temps sont deux cartes de la même rubrique.
 *
 * Arithmétique entière exacte (le balayage travaille en centièmes) : aucun
 * arrondi n'est jamais affiché ni utilisé pour trancher une égalité.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 940;

const L_TABLEUR = 'Conjecturer avec un tableur';
const L_ETIRER = 'Une formule à étirer';
const L_DEFAUT = 'Quand la régularité se casse';
const L_BALAYAGE = 'Encadrer la solution par balayage';
const L_PSEUDOCODE = 'Conjecturer avec un algorithme';
const L_MOTIFS = 'Suite de motifs et tableau';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement)

/**
 * Une lettre de variable s'écrit en italique mathématique, jamais en lettre
 * ordinaire (règle d'équipe, retour prof 2026-09-24). Deux rendus, deux écritures :
 *   - ÉNONCÉ et LIGNES DE TABLEAU : la regex de rendu est STRICTE, le fragment doit
 *     commencer par une commande LaTeX → `tex()` pose le `\mathrm{}` de largeur nulle ;
 *   - CHOIX de QCM et PHRASES À TROUS : regex PERMISSIVE, le « $x$ » nu suffit.
 *
 * ⚠️ Ce générateur mêle DEUX sortes de lettres, et une seule est une variable :
 *   - « x », « n » désignent un nombre → italique ;
 *   - « A1 », « B1 », « =A1*A1+3 » sont des références et des formules de TABLEUR,
 *     et les lignes de pseudo-code sont du texte d'algorithme : ce sont des mots
 *     d'outil, que l'élève recopie tels quels. Ils restent en caractère droit.
 */
const enLatex = (s) => String(s)
    .replace(/²/g, '^2')          // « x² » n'est pas du LaTeX : KaTeX le refuserait
    .replace(/−/g, '-')
    .replace(/\s*×\s*/g, ' \\times ')
    .replace(/(\d),(\d)/g, '$1{,}$2');
const mathEnonce = (s) => tex(enLatex(s));
const mathChoix = (s) => `$${enLatex(s)}$`;
const lettreTrou = (L) => `$${L}$`;

// ─── Bornes de la spécification ─────────────────────────────────────────
const CAS_MIN = 5;    // variables.nombre_de_cas_explores.min (le max, 20, n'est jamais atteint)
const DEPART_MAX = 10;
const COEF_MIN = 1;
const COEF_MAX = 10;
const PRECISIONS = [1, 0.1, 0.01];

// table d'énoncé HTML (idiome maison) — une feuille de calcul, pas une capture
// `text-align:center` est explicite : la feuille de styles de l'application aligne les
// en-têtes à gauche, et le centrage par défaut de <th> ne s'applique donc pas.
const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;'
    + 'font-weight:600;text-align:center';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
/**
 * Une feuille de calcul : les en-têtes sont des COLONNES et les valeurs descendent.
 *
 * Les énoncés de ce générateur parlent de « la colonne A », de « la colonne B affiche »
 * et d'une formule « recopiée vers le bas ». Le tableau, lui, posait A, C et D en
 * LIGNES : l'élève lisait des lignes là où le texte nommait des colonnes, et le geste
 * de recopie vers le bas n'avait rien à quoi se rattacher (retour prof 2026-09-25).
 *
 * @param {string[]} entetes - un en-tête par colonne
 * @param {Array<Array<string|number>>} lignes - une ligne de valeurs par rang
 */
const tableColonnes = (entetes, lignes) =>
    '<table style="border-collapse:collapse;margin:12px auto">'
    + `<tr>${entetes.map(e => `<th style="${T_TH}">${e}</th>`).join('')}</tr>`
    + lignes.map(l => `<tr>${l.map(c => `<td style="${T_TD}">${c}</td>`).join('')}</tr>`).join('')
    + '</table>';

/** Affichage d'un relatif : le vrai signe moins. */
const aff = (v) => String(v).replace('-', MOINS);
/** Un nombre de centièmes en écriture française : 430 → « 4,3 ». */
const frC = (c) => {
    const n = Math.abs(c);
    const ent = Math.floor(n / 100);
    const dec = n % 100;
    const corps = dec === 0 ? `${ent}`
        : (dec % 10 === 0 ? `${ent},${dec / 10}` : `${ent},${String(dec).padStart(2, '0')}`);
    return (c < 0 ? MOINS : '') + corps;
};

const CONVENTION = 'Écris la formule en commençant par le signe égal, avec la lettre de '
    + 'la colonne puis le numéro de la ligne (par exemple A1), et le signe × noté *.';

// ═════════ Carte 1 — Conjecturer avec un tableur (niveaux 2 et 3) ═════════

const TEXTE_TABLEUR = 'Dans un tableur, la colonne A contient des nombres entiers.';

/** La formule d'une colonne, avec ses écritures admises et ses pièges nommés. */
const tirerFormule = () => {
    const cible = randomChoice(['carre', 'lineaire']);
    const a1 = randInt(2, 9);      // la valeur affichée en A1, pour le piège « valeurs »
    if (cible === 'carre') {
        const p = randInt(COEF_MIN, COEF_MAX);
        return {
            cible, p, a1, expr: `x² + ${p}`,
            correct: `=A1*A1+${p}`,
            ecritures: [`=A1*A1+${p}`, `=${p}+A1*A1`],
            candidats: [
                { valeur: `=A1A1+${p}`, code: 'resultat_pris_pour_formule' },
                { valeur: `=${a1}*${a1}+${p}`, code: 'valeurs_au_lieu_de_references' },
                // ⚠️ La référence ABSOLUE s'écrit « $A$1 », et le signe dollar est le
                // délimiteur des formules mathématiques à l'affichage : le rendu avalait
                // les dollars et cette proposition s'affichait « =A1*A1+8 », c'est-à-dire
                // la BONNE RÉPONSE (signalé par le prof le 2026-09-24). Un élève qui la
                // choisissait était compté faux pour avoir reconnu la bonne formule.
                // La référence qui se décale d'une ligne sert la même erreur de la spec,
                // « voir se décaler une valeur qui devait rester fixe », sans dollar.
                { valeur: `=A2*A2+${p}`, code: 'reference_relative_ou_absolue' },
                { valeur: `=A1+A1+${p}`, code: 'un_seul_cas' },
            ],
        };
    }
    const k = randInt(2, COEF_MAX);
    const c = randInt(COEF_MIN, COEF_MAX);
    return {
        cible, k, c, a1, expr: `${k}x + ${c}`,
        correct: `=${k}*A1+${c}`,
        ecritures: [`=${k}*A1+${c}`, `=A1*${k}+${c}`, `=${c}+${k}*A1`, `=${c}+A1*${k}`],
        candidats: [
            { valeur: `=${k}A1+${c}`, code: 'resultat_pris_pour_formule' },
            { valeur: `=${k}*${a1}+${c}`, code: 'valeurs_au_lieu_de_references' },
            // Même raison que pour le cas du carré : pas de dollar dans un texte affiché.
            { valeur: `=${k}*A2+${c}`, code: 'reference_relative_ou_absolue' },
            { valeur: `=A1+${k}+${c}`, code: 'un_seul_cas' },
        ],
    };
};

const genTableurFormuleSaisie = () => {
    const t = tirerFormule();
    const payload = makeSaisie(t.correct,
        `<p><b>${TEXTE_TABLEUR} A1 contient ${t.a1}. Quelle formule faut-il saisir en B1 `
        + `pour calculer ${mathEnonce(t.expr)}, où ${mathEnonce('x')} est le nombre `
        + `de la cellule A1 ?</b> La formule sera recopiée vers le bas. ${CONVENTION}</p>`,
        { ...opts(L_TABLEUR), comparaison: 'expression', reponses_acceptees: t.ecritures });
    payload._meta.params = {
        ...t, outil: 'tableur', niveau: 2, casExplores: 6, colonnes: 1,
        erreurVisee: ['valeurs_au_lieu_de_references', 'reference_relative_ou_absolue'],
    };
    return payload;
};

const genTableurFormuleQcm = () => {
    const t = tirerFormule();
    const distracteurs = shuffle(t.candidats).slice(0, 3);
    const payload = makeQcm([t.correct, ...distracteurs.map(d => d.valeur)], t.correct,
        `<p><b>${TEXTE_TABLEUR} A1 contient ${t.a1}. Quelle formule faut-il saisir en B1 `
        + `pour calculer ${mathEnonce(t.expr)}, où ${mathEnonce('x')} est le nombre `
        + 'de la cellule A1 ?</b> La formule sera recopiée vers le bas.</p>',
        opts(L_TABLEUR));
    payload._meta.params = {
        ...t, outil: 'tableur', niveau: 2, casExplores: 6, colonnes: 1, distracteurs,
    };
    return payload;
};

/** L'identité (x + p)(x − p) = x² − p², explorée sur CINQ valeurs au moins. */
const tirerIdentite = () => {
    const p = randInt(1, 5);
    const debut = randInt(0, DEPART_MAX - CAS_MIN);
    const xs = Array.from({ length: CAS_MIN }, (_, i) => debut + i);
    return { p, xs, C: (x) => (x + p) * (x - p), D: (x) => x * x - p * p };
};

// Le tableau est du HTML posé dans l'ÉNONCÉ : ses en-têtes suivent donc la règle
// stricte du rendu d'énoncé, d'où mathEnonce et non le « $x$ » nu.
const tableIdentite = (t, trousC = [], trousD = []) => tableColonnes(
    [
        `A (nombre ${mathEnonce('x')})`,
        `C : ${mathEnonce(`(x + ${t.p})(x ${MOINS} ${t.p})`)}`,
        `D : ${mathEnonce(`x² ${MOINS} ${t.p * t.p}`)}`,
    ],
    t.xs.map((x, i) => [
        String(x),
        trousC.includes(i) ? '…' : aff(t.C(x)),
        trousD.includes(i) ? '…' : aff(t.D(x)),
    ]),
);

const genTableurTrous = () => {
    const t = tirerIdentite();
    const idx = t.xs.map((_, i) => i);
    const trousC = shuffle(idx).slice(0, 2).sort();
    const trousD = shuffle(idx).slice(0, 2).sort();
    const negatifPossible = t.xs.some((x, i) => (trousC.includes(i) || trousD.includes(i)) && t.C(x) < 0);
    // Une cellule par ligne (l'impression coupait une ligne portant deux trous).
    const x = lettreTrou('x');
    const phrase = [
        ...trousC.map(i => `Pour ${x} = ${t.xs[i]}, la cellule C affiche {{${t.C(t.xs[i])}}}`),
        ...trousD.map(i => `Pour ${x} = ${t.xs[i]}, la cellule D affiche {{${t.D(t.xs[i])}}}`),
    ].join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>${TEXTE_TABLEUR} Complète les colonnes C et D du tableau.</b>`
        + (negatifPossible ? ' Écris les nombres négatifs avec leur signe (par exemple -3).' : '')
        + `</p>\n${tableIdentite(t, trousC, trousD)}`,
        { ...opts(L_TABLEUR), mode: 'saisie' });
    payload._meta.params = {
        p: t.p, xs: t.xs, trousC, trousD, outil: 'tableur', niveau: 3,
        casExplores: t.xs.length, colonnes: 2, erreurVisee: ['un_seul_cas'],
    };
    return payload;
};

/**
 * Trois RELATIONS entre les colonnes C et D, toutes vraies pour n'importe quel nombre.
 *
 * La carte n'en proposait qu'une, l'égalité, si bien que la bonne phrase ne changeait
 * jamais : 400 tirages, une seule réponse juste (retour prof 2026-09-25). Le tableau,
 * lui, changeait, donc le contrôle de variété du harnais passait — il compte les
 * tirages, pas les réponses. Un élève qui avait vu la carte deux fois cliquait la bonne
 * phrase sans lire le tableau.
 *
 * `relation` est la phrase juste, `dementi` une phrase FAUSSE du même genre : celle
 * d'une autre famille, qui décrit une relation que le tableau contredit.
 */
const FAMILLES_CONJECTURE = [
    {
        id: 'egalite',
        tirer: () => ({ p: randInt(1, 5) }),
        nomC: (v) => `(x + ${v.p})(x ${MOINS} ${v.p})`,
        nomD: (v) => `x² ${MOINS} ${v.p * v.p}`,
        C: (x, v) => (x + v.p) * (x - v.p),
        D: (x, v) => x * x - v.p * v.p,
        relation: () => 'les colonnes C et D affichent la même valeur',
        verifie: (c, d) => d === c,
    },
    {
        id: 'double',
        tirer: () => ({ p: randInt(1, 6) }),
        nomC: (v) => `x + ${v.p}`,
        nomD: (v) => `2x + ${2 * v.p}`,
        C: (x, v) => x + v.p,
        D: (x, v) => 2 * x + 2 * v.p,
        relation: () => 'la colonne D affiche le double de la colonne C',
        verifie: (c, d) => d === 2 * c,
    },
    {
        id: 'ecart',
        tirer: () => ({ k: randInt(2, 9) }),
        nomC: () => 'x × x',
        nomD: (v) => `x × x + ${v.k}`,
        C: (x) => x * x,
        D: (x, v) => x * x + v.k,
        relation: (v) => `la colonne D affiche la colonne C augmentée de ${v.k}`,
        verifie: (c, d, v) => d === c + v.k,
    },
];

const tirerConjecture = () => {
    const fam = randomChoice(FAMILLES_CONJECTURE);
    const v = fam.tirer();
    const debut = randInt(0, DEPART_MAX - CAS_MIN);
    const xs = Array.from({ length: CAS_MIN }, (_, i) => debut + i);
    // Les démentis : les relations des DEUX autres familles, que ce tableau contredit.
    // Il en faut deux depuis que la proposition sans quantificateur a été retirée
    // (décision prof 2026-09-25) : elle disait la même chose que la bonne réponse, donc
    // elle était vraie, et aucune question ne pouvait honnêtement l'écarter.
    const [autre, autre2] = shuffle(FAMILLES_CONJECTURE.filter(f => f.id !== fam.id));
    const vAutre = autre.tirer();
    const vAutre2 = autre2.tirer();
    return {
        fam, v, xs, autre, vAutre, autre2, vAutre2,
        dementi: autre.relation(vAutre), dementi2: autre2.relation(vAutre2),
    };
};

const tableConjecture = (t) => tableColonnes(
    [
        `A (nombre ${mathEnonce('x')})`,
        `C : ${mathEnonce(t.fam.nomC(t.v))}`,
        `D : ${mathEnonce(t.fam.nomD(t.v))}`,
    ],
    t.xs.map(x => [String(x), aff(t.fam.C(x, t.v)), aff(t.fam.D(x, t.v))]),
);

const genTableurConjectureQcm = () => {
    // Des PHRASES : seule la lettre passe en italique, jamais la phrase entière.
    const x = lettreTrou('x');
    let t; let dit; let correct; let candidats; let choix;
    for (let essai = 0; essai < 20; essai++) {
        t = tirerConjecture();
        dit = t.fam.relation(t.v);
        const majuscule = `${dit.charAt(0).toUpperCase()}${dit.slice(1)}`;
        correct = `Pour tout nombre ${x}, ${dit}.`;
        candidats = [
            { valeur: `${majuscule} pour ${x} = ${t.xs[0]}.`, code: 'conjecture_pour_une_valeur' },
            { valeur: `Pour tout nombre ${x}, ${t.dementi}.`, code: 'un_seul_cas' },
            { valeur: `Pour tout nombre ${x}, ${t.dementi2}.`, code: 'un_seul_cas' },
        ];
        choix = [correct, ...candidats.map(c => c.valeur)];
        if (new Set(choix).size === 4) break;
    }
    const payload = makeQcm(choix, correct,
        // Le critère est posé DANS la question. Les propositions « pour une valeur » et
        // « sans quantification » sont VRAIES par nature — c'est justement l'erreur
        // qu'elles servent —, elles ne peuvent donc être écartées que parce qu'elles ne
        // répondent pas à ce qui est demandé (retour prof 2026-09-25).
        `<p><b>${TEXTE_TABLEUR} Observe les colonnes C et D.</b> Quelle phrase est vraie `
        + `<b>pour n'importe quel nombre</b> ?</p>\n${tableConjecture(t)}`,
        opts(L_TABLEUR));
    payload._meta.params = {
        famille: t.fam.id, ...t.v,
        familleDementi: t.autre.id, vDementi: t.vAutre,
        familleDementi2: t.autre2.id, vDementi2: t.vAutre2,
        xs: t.xs, outil: 'tableur', niveau: 3,
        casExplores: t.xs.length, colonnes: 2, distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 2 — Une formule à étirer (niveaux 1 et 2) ═════════

// Familles à conclusion UNIQUE : jamais un pas multiple de 2 ET de 3, sans quoi
// deux conclusions seraient également vraies.
const FAMILLES_SUITE = [
    { concl: 'impairs', tirer: () => ({ a0: randomChoice([1, 3, 5, 7, 9, 11, 13, 15]), r: 2 }) },
    { concl: 'pairs', tirer: () => ({ a0: randomChoice([2, 4, 6, 8, 10, 12, 14]), r: 2 }) },
    { concl: 'multiples de 3', tirer: () => ({ a0: randomChoice([3, 6, 9, 12, 15]), r: 3 }) },
    { concl: 'multiples de 5', tirer: () => ({ a0: randomChoice([5, 10, 15, 20]), r: 5 }) },
];
const CONCLUSIONS = ['impairs', 'pairs', 'multiples de 3', 'multiples de 5'];

const TEXTE_ETIRER = (a0, r) =>
    `<p><b>Dans un tableur, la cellule A1 contient ${a0}. On saisit en A2 la formule `
    + `=A1+${r}, puis on la recopie vers le bas jusqu'en A6.</b> `;

const genEtirerTrous = () => {
    const fam = randomChoice(FAMILLES_SUITE);
    const { a0, r } = fam.tirer();
    const vals = [1, 2, 3, 4, 5].map(i => a0 + i * r);
    const phrase = vals.map((v, i) => `A${i + 2} = {{${v}}}`).join('\n');
    const payload = makeTexteTrou(phrase,
        TEXTE_ETIRER(a0, r) + 'Quelles valeurs affichent les cellules A2 à A6 ?</p>',
        { ...opts(L_ETIRER), mode: 'saisie' });
    payload._meta.params = {
        concl: fam.concl, a0, r, outil: 'tableur', niveau: 2, casExplores: 6, colonnes: 1,
        erreurVisee: ['resultat_pris_pour_formule'],
    };
    return payload;
};

const genEtirerConclQcm = () => {
    const fam = randomChoice(FAMILLES_SUITE);
    const { a0, r } = fam.tirer();
    const vals = [a0, ...[1, 2, 3, 4, 5].map(i => a0 + i * r)];
    const correct = `Tous les nombres de la colonne A sont des nombres ${fam.concl}.`;
    const candidats = [
        ...shuffle(CONCLUSIONS.filter(c => c !== fam.concl)).slice(0, 2)
            .map(c => ({ valeur: `Tous les nombres de la colonne A sont des nombres ${c}.`, code: 'un_seul_cas' })),
        { valeur: `Le nombre de la cellule A6 est un nombre ${fam.concl}.`, code: 'conjecture_pour_une_valeur' },
    ];
    const payload = makeQcm([correct, ...candidats.map(c => c.valeur)], correct,
        // « Le nombre de la cellule A6 est un multiple de 5 » est VRAI : c'est l'erreur
        // visée, énoncer la conjecture pour la seule valeur regardée. La question dit
        // donc ce qu'elle attend, une phrase qui vaut pour TOUS les nombres.
        TEXTE_ETIRER(a0, r) + `La colonne A affiche : ${vals.join(' ; ')}. `
        + 'Quelle phrase décrit TOUS les nombres de la colonne A, et pas un seul '
        + 'd\'entre eux ?</p>',
        opts(L_ETIRER));
    payload._meta.params = {
        concl: fam.concl, a0, r, outil: 'tableur', niveau: 1, casExplores: 6, colonnes: 1,
        distracteurs: candidats,
    };
    return payload;
};

const genEtirerFormuleQcm = () => {
    const k = randomChoice([2, 3]);
    const nomK = k === 2 ? 'le double' : 'le triple';
    const a0 = randInt(1, 12);
    const correct = `=${k}*A1`;
    const candidats = [
        { valeur: `=A1+${k}`, code: 'un_seul_cas' },
        { valeur: `${k}*A1`, code: 'resultat_pris_pour_formule' },
        { valeur: `=${k}*${a0}`, code: 'valeurs_au_lieu_de_references' },
    ];
    const payload = makeQcm([correct, ...candidats.map(c => c.valeur)], correct,
        `<p><b>Dans un tableur, la colonne A contient des nombres (A1 = ${a0}, etc.). `
        + `Quelle formule saisir en B1, puis recopier vers le bas, pour afficher ${nomK} `
        + 'de chaque nombre de la colonne A ?</b></p>',
        opts(L_ETIRER));
    payload._meta.params = {
        k, a0, outil: 'tableur', niveau: 2, casExplores: 6, colonnes: 1, distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 3 — Quand la régularité se casse (niveau 4) ═════════

/**
 * Deux colonnes : B = k·n et C = n². B dépasse C tant que n < k, puis C
 * dépasse B. Le rang charnière k est TOUJOURS dans la plage affichée
 * (exclusions 6 et points_de_vigilance 5).
 */
const tirerDefaut = () => {
    const debut = randomChoice([1, 2]);
    // au moins CAS_MIN lignes affichées, et le rang charnière k + 1 parmi elles
    const k = randInt(3 + debut, 9);
    // la colonne du carré est tantôt la deuxième, tantôt la troisième
    const carreEnB = randomChoice([true, false]);
    // Le tableau va JUSQU'À k + 3, soit trois lignes après la bascule, et non jusqu'à
    // k + 1 comme avant. Il s'arrêtait pile au rang où le régime change : on n'en voyait
    // qu'une seule ligne, si bien que « la colonne B dépasse la colonne C pour n = 10 »
    // décrivait le tableau aussi fidèlement que « à partir du rang n = 10 », et
    // l'affirmation attendue extrapolait au-delà de ce qui était montré (retour prof
    // 2026-09-25). Avec trois lignes du nouveau régime, la phrase restreinte à un rang
    // ne rend plus compte du tableau, et la phrase générale s'y appuie vraiment.
    const xs = Array.from({ length: k + 4 - debut }, (_, i) => debut + i);  // jusqu'à k + 3
    return {
        k, debut, carreEnB, xs,
        nomB: carreEnB ? 'n²' : `${k}n`,
        nomC: carreEnB ? `${k}n` : 'n²',
        B: (n) => (carreEnB ? n * n : k * n),
        C: (n) => (carreEnB ? k * n : n * n),
        // la colonne du carré finit par dépasser l'autre, à partir du rang k + 1
        colonneQuiDepasse: carreEnB ? 'B' : 'C',
        colonneDepassee: carreEnB ? 'C' : 'B',
    };
};

const genDefautQcm = () => {
    const t = tirerDefaut();
    const n = lettreTrou('n');
    const tableau = tableColonnes(
        [`A (rang ${mathEnonce('n')})`, `B : ${mathEnonce(t.nomB)}`, `C : ${mathEnonce(t.nomC)}`],
        t.xs.map(v => [String(v), String(t.B(v)), String(t.C(v))]),
    );
    const correct = `À partir du rang ${n} = ${t.k + 1}, la colonne ${t.colonneQuiDepasse} `
        + `dépasse la colonne ${t.colonneDepassee}.`;
    const candidats = [
        { valeur: `Pour tout rang ${n}, la colonne ${t.colonneDepassee} dépasse la colonne ${t.colonneQuiDepasse}.`, code: 'contre_exemple_ignore' },
        { valeur: `Pour tout rang ${n}, la colonne ${t.colonneQuiDepasse} dépasse la colonne ${t.colonneDepassee}.`, code: 'contre_exemple_ignore' },
        { valeur: `La colonne ${t.colonneQuiDepasse} dépasse la colonne ${t.colonneDepassee} pour ${n} = ${t.k + 1}.`, code: 'conjecture_pour_une_valeur' },
    ];
    const payload = makeQcm([correct, ...candidats.map(c => c.valeur)], correct,
        // Ici aussi la proposition restreinte à un rang est VRAIE : la question demande
        // donc une phrase qui rende compte de toutes les lignes, pas d'une seule.
        '<p><b>Dans un tableur, la colonne A contient les rangs, la colonne B affiche '
        + `${mathEnonce(t.nomB)} et la colonne C affiche ${mathEnonce(t.nomC)}.</b> Lis bien `
        + 'TOUTES les lignes. Quelle phrase rend compte de ce qui se passe sur l\'ensemble '
        + `du tableau, et pas à un seul rang ?</p>\n${tableau}`,
        opts(L_DEFAUT));
    payload._meta.params = {
        ...t, outil: 'tableur', niveau: 4, casExplores: t.xs.length, colonnes: 2,
        regulariteEnDefaut: true, distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 4 — Encadrer la solution par balayage (niveau 5) ═════════

/**
 * L'équation a·x + b = c, de solution NON entière. Tout est calculé en
 * centièmes entiers : aucune valeur arrondie n'est affichée, et aucune ne sert
 * à trancher (exclusions 7).
 */
const tirerBalayage = () => {
    for (let essai = 0; essai < 200; essai++) {
        const a = randInt(2, 9);
        const b = randInt(COEF_MIN, COEF_MAX);
        const c = randInt(b + 2, b + 60);
        const num = c - b;                    // solution = num / a
        if (num % a === 0) continue;          // la solution doit être non entière
        // la solution n'est jamais un nombre EXACT de centièmes : tous les
        // encadrements restent stricts, aucun arrondi ne tranche une égalité
        if ((num * 100) % a === 0) continue;
        const solC = Math.floor((num * 100) / a);
        const precision = randomChoice(PRECISIONS);
        const pas = Math.round(precision * 100);            // en centièmes
        const basC = Math.floor(solC / pas) * pas;
        const hautC = basC + pas;
        if (basC < pas) continue;             // le distracteur « trop large » reste positif
        if (solC < 300) continue;             // le balayage à l'unité tient sur cinq lignes
        return { a, b, c, num, solC, precision, pas, basC, hautC };
    }
    return { a: 3, b: 7, c: 20, num: 13, solC: 433, precision: 0.1, pas: 10, basC: 430, hautC: 440 };
};

/** a·x + b calculé en centièmes pour un x donné en centièmes. */
const membreC = (t, xC) => t.a * xC + t.b * 100;

const genBalayageTrous = () => {
    const t = tirerBalayage();
    const unite = Math.floor(t.solC / 100);
    // cinq valeurs balayées autour de la solution, puis l'encadrement à l'unité
    const xs = [unite - 2, unite - 1, unite, unite + 1, unite + 2];
    // Chaque ligne mêle du français et des maths : elle ne commence ni ne finit par
    // « $ », elle reste donc en rendu inline et garde ses champs de saisie.
    const phrase = xs.map(x => `Pour ${lettreTrou('x')} = ${x}, `
        + `${mathChoix(`${t.a}x + ${t.b}`)} vaut {{${t.a * x + t.b}}}`).join('\n')
        + `\nLa solution est donc comprise entre {{${unite}}} et {{${unite + 1}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>On cherche la solution de l'équation ${mathEnonce(`${t.a}x + ${t.b} = ${t.c}`)} `
        + `en balayant les valeurs de ${mathEnonce('x')} dans un tableur, de 1 en 1.</b> `
        + 'Complète le balayage, puis donne l\'encadrement à l\'unité.</p>',
        { ...opts(L_BALAYAGE), mode: 'saisie' });
    payload._meta.params = {
        ...t, xs, unite, precision: 1, pas: 100, basC: unite * 100, hautC: (unite + 1) * 100,
        outil: 'tableur', niveau: 5, casExplores: xs.length, colonnes: 1,
        erreurVisee: ['encadrement_incomplet', 'approche_pris_pour_exact'],
    };
    return payload;
};

const genBalayageQcm = () => {
    const t = tirerBalayage();
    const lignes = [];
    for (let i = -1; i <= 3; i++) {          // cinq lignes (nombre_de_cas_explores min)
        const xC = t.basC + i * t.pas;
        if (xC < 0) continue;
        lignes.push({ xC, vC: membreC(t, xC) });
    }
    const tableau = tableColonnes(
        [mathEnonce('x'), mathEnonce(`${t.a}x + ${t.b}`)],
        lignes.map(l => [frC(l.xC), frC(l.vC)]),
    );
    const correct = mathChoix(`${frC(t.basC)} < x < ${frC(t.hautC)}`);
    const candidats = [
        { valeur: mathChoix(`x = ${frC(t.basC)}`), code: 'approche_pris_pour_exact' },
        { valeur: mathChoix(`x > ${frC(t.basC)}`), code: 'encadrement_incomplet' },
        { valeur: mathChoix(`${frC(t.basC - t.pas)} < x < ${frC(t.hautC + t.pas)}`), code: 'encadrement_incomplet' },
    ];
    const choix = [correct, ...candidats.map(c => c.valeur)];
    const payload = makeQcm(choix, correct,
        `<p><b>On balaye les valeurs de ${mathEnonce('x')} de ${frC(t.pas)} en ${frC(t.pas)} `
        + `pour encadrer la solution de ${mathEnonce(`${t.a}x + ${t.b} = ${t.c}`)}.</b> `
        + `Quel encadrement le tableau donne-t-il ?</p>\n${tableau}`,
        opts(L_BALAYAGE));
    payload._meta.params = {
        ...t, outil: 'tableur', niveau: 5, casExplores: lignes.length, colonnes: 1,
        distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 5 — Conjecturer avec un algorithme (niveau 6) ═════════

/**
 * Pseudo-code FRANÇAIS : affectation, boucle, test. Aucun logiciel n'est nommé,
 * aucune syntaxe n'est exigée (exclusions 3 et 5).
 */
const FAMILLES_ALGO = [
    {
        id: 'boucleSomme', k: 4, concl: 'un multiple de 4',
        lignes: (L) => [
            `Demander un nombre, on l'appelle ${L}.`,
            `Mettre R à ${L}.`,
            `Répéter 3 fois : mettre R à R + ${L}.`,
            'Afficher R.',
        ],
        f: (n) => 4 * n,
        distr: [
            { code: 'initialisation_oubliee', texte: (L) => `Le résultat affiché est le triple de ${L}.` },
            { code: 'un_seul_cas', texte: () => 'Pour tout nombre choisi, le résultat affiché est un nombre impair.' },
            { code: 'un_seul_cas', texte: () => 'Pour tout nombre choisi, le résultat affiché est un multiple de 3.' },
        ],
    },
    {
        id: 'bouclePair', k: 2, concl: 'un nombre pair',
        lignes: (L) => [
            `Demander un nombre entier, on l'appelle ${L}.`,
            'Mettre R à 0.',
            `Répéter 2 fois : mettre R à R + ${L}.`,
            'Si R est positif, afficher R, sinon afficher 0.',
        ],
        f: (n) => 2 * n,
        distr: [
            { code: 'initialisation_oubliee', texte: (L) => `Le résultat affiché est le triple de ${L}.` },
            { code: 'un_seul_cas', texte: () => 'Pour tout nombre choisi, le résultat affiché est un multiple de 5.' },
            { code: 'un_seul_cas', texte: () => 'Pour tout nombre choisi, le résultat affiché est un nombre impair.' },
        ],
    },
    {
        id: 'boucleTriple', k: 3, concl: 'un multiple de 3',
        lignes: (L) => [
            `Demander un nombre entier, on l'appelle ${L}.`,
            'Mettre R à 0.',
            `Répéter 3 fois : mettre R à R + ${L}.`,
            'Afficher R.',
        ],
        f: (n) => 3 * n,
        distr: [
            { code: 'initialisation_oubliee', texte: (L) => `Le résultat affiché est le quadruple de ${L}.` },
            { code: 'un_seul_cas', texte: () => 'Pour tout nombre choisi, le résultat affiché est un multiple de 4.' },
            { code: 'un_seul_cas', texte: () => 'Pour tout nombre choisi, le résultat affiché est un nombre pair.' },
        ],
    },
];

const LETTRES_ALGO = ['n', 'x', 'y', 't'];

const blocAlgo = (fam, L) =>
    '<div style="background:#F1F5F9;border-left:4px solid #1E3A8A;padding:10px 14px;'
    + 'margin:12px auto;max-width:460px">'
    + fam.lignes(L).join('<br>') + '</div>';

const genPseudocodeTrous = () => {
    const fam = randomChoice(FAMILLES_ALGO);
    const L = randomChoice(LETTRES_ALGO);
    const entrees = shuffle([3, 4, 5, 6, 7, 8, 10, 12]).slice(0, CAS_MIN);
    const phrase = entrees.map(n => `Pour ${L} = ${n}, le programme affiche {{${fam.f(n)}}}`).join('\n');
    const payload = makeTexteTrou(phrase,
        '<p><b>Voici un algorithme écrit en français.</b> Exécute-le pour chacune des '
        + `valeurs proposées.</p>${blocAlgo(fam, L)}`,
        { ...opts(L_PSEUDOCODE), mode: 'saisie' });
    payload._meta.params = {
        fam: fam.id, L, entrees, outil: 'algorithme', niveau: 6,
        casExplores: entrees.length, colonnes: 1,
        erreurVisee: ['initialisation_oubliee'],
    };
    return payload;
};

const genPseudocodeQcm = () => {
    const fam = randomChoice(FAMILLES_ALGO);
    const L = randomChoice(LETTRES_ALGO);
    const entrees = shuffle([3, 4, 5, 6, 7, 8, 10, 12]).slice(0, CAS_MIN).sort((a, b) => a - b);
    const correct = `Pour tout nombre choisi, le résultat affiché est ${fam.concl}.`;
    // Une proposition VRAIE mais restreinte au premier nombre essayé. Elle remplace
    // l'ancienne proposition sans quantificateur, retirée le 2026-09-25 : celle-là
    // disait la même chose que la bonne réponse, donc rien ne pouvait l'écarter
    // honnêtement. Celle-ci annonce elle-même qu'elle ne parle que d'un cas, et la
    // question demande une règle valable pour n'importe quel nombre.
    const candidats = [
        ...fam.distr.map(d => ({ valeur: d.texte(L), code: d.code })).slice(0, 2),
        {
            valeur: `Pour le nombre ${entrees[0]}, le résultat affiché est ${fam.concl}.`,
            code: 'conjecture_pour_une_valeur',
        },
    ];
    const choix = [correct, ...candidats.map(c => c.valeur)];
    const payload = makeQcm(choix, correct,
        '<p><b>Voici un algorithme écrit en français.</b> Pour les nombres '
        + `${entrees.join(', ')}, il affiche ${entrees.map(n => fam.f(n)).join(', ')}.</p>`
        // Le critère est simple depuis que la proposition sans quantificateur a été
        // retirée : les trois propositions fausses le sont, et la seule vraie qui reste
        // à écarter se restreint d'elle-même à un nombre. Une question courte suffit
        // donc, là où il avait fallu un moment ajouter « et qui le dit » — formule que
        // le prof a jugée obscure, à juste titre (2026-09-25).
        + `${blocAlgo(fam, L)}<p>Quelle phrase est vraie <b>pour n'importe quel nombre `
        + 'choisi</b> ?</p>',
        opts(L_PSEUDOCODE));
    payload._meta.params = {
        fam: fam.id, L, entrees, outil: 'algorithme', niveau: 6,
        casExplores: entrees.length, colonnes: 1, distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 6 — Suite de motifs et tableau (variante transverse) ═════════

// kn + c : n rangées pleines et une rangée du bas allongée ou raccourcie de |c|.
const FAMILLES_MOTIF = [];
[1, 2, 3].forEach(k => [0, 1, 2, 3].forEach(c => {
    if (k === 1 && c === 0) return;                  // la suite n : pas de motif lisible
    FAMILLES_MOTIF.push({
        k, c,
        formule: `${k === 1 ? 'n' : `${k}n`}${c === 0 ? '' : ` + ${c}`}`,
    });
}));
FAMILLES_MOTIF.push({ k: 2, c: -1, formule: `2n ${MOINS} 1` });
FAMILLES_MOTIF.push({ k: 3, c: -1, formule: `3n ${MOINS} 1` });

const svgMotifs = (fam, rangs) => {
    const s = 13;
    const parts = [];
    let y = 18;
    rangs.forEach(etape => {
        const yEtape = y;
        parts.push(`<text x='4' y='${yEtape + s}' font-size='12' fill='#1E3A8A' font-family='sans-serif'>rang ${etape}</text>`);
        const rangees = [];
        for (let r = 0; r < fam.k - 1; r++) rangees.push(etape);
        rangees.push(etape + fam.c);
        rangees.forEach((nb, r) => {
            for (let i = 0; i < nb; i++) {
                parts.push(`<rect x='${58 + i * (s + 2)}' y='${yEtape + r * (s + 2)}' width='${s}' height='${s}' fill='#C7D2FE' stroke='#1E3A8A' stroke-width='1.5'/>`);
            }
        });
        y += rangees.length * (s + 2) + 12;
    });
    const largeur = 58 + (Math.max(...rangs) + Math.max(fam.c, 0)) * (s + 2) + 10;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${largeur} ${y}'>${parts.join('')}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const genMotifsQcm = () => {
    const fam = randomChoice(FAMILLES_MOTIF);
    // les motifs DESSINÉS sont exactement ceux que le tableau compte : l'élève
    // ne lit jamais dans le tableau une valeur qu'il ne pourrait pas compter
    const debut = randomChoice([1, 2]);
    const rangs = Array.from({ length: CAS_MIN }, (_, i) => debut + i);
    const compte = (n) => fam.k * n + fam.c;
    const n = lettreTrou('n');
    const tableau = tableColonnes(
        [`Rang ${mathEnonce('n')}`, 'Nombre de carrés'],
        rangs.map(v => [String(v), String(compte(v))]),
    );
    const correct = `Pour tout rang ${n}, le nombre de carrés est ${mathChoix(fam.formule)}.`;
    const candidats = [
        {
            // le coefficient du rang majoré de 1 : faux dès le premier rang.
            // Le terme constant garde l'écriture de la famille, signe compris.
            valeur: `Pour tout rang ${n}, le nombre de carrés est `
                + `${mathChoix(`${fam.k + 1}n${fam.c === 0 ? '' : (fam.c > 0 ? ` + ${fam.c}` : ` ${MOINS} ${-fam.c}`)}`)}.`,
            code: 'un_seul_cas',
        },
        { valeur: `Au rang ${rangs[rangs.length - 1]}, le nombre de carrés est ${compte(rangs[rangs.length - 1])}.`, code: 'conjecture_pour_une_valeur' },
        {
            // la règle sans son terme constant, ou décalée d'une unité si elle n'en a pas
            valeur: `Pour tout rang ${n}, le nombre de carrés est `
                + `${mathChoix(fam.c === 0 ? `${fam.k}n + 1` : (fam.k === 1 ? 'n' : `${fam.k}n`))}.`,
            code: 'un_seul_cas',
        },
    ];
    const choix = [correct, ...candidats.map(c => c.valeur)];
    const payload = makeQcm(choix, correct,
        `<p><b>On construit une suite de motifs avec des carrés (rangs ${rangs[0]} à `
        + `${rangs[rangs.length - 1]} ci-dessous). On a porté le nombre de carrés dans un `
        // Même critère que la carte de l'algorithme, dans les termes du rang.
        + `tableur.</b> Quelle phrase est vraie <b>pour tous les rangs</b> ?</p>\n${tableau}`,
        { ...opts(L_MOTIFS), elements_additionnels: svgMotifs(fam, rangs) });
    payload._meta.params = {
        fam, rangs, debut, outil: 'tableur', niveau: 1, casExplores: rangs.length, colonnes: 1,
        distracteurs: candidats,
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    tableur_formule_texte: genTableurFormuleSaisie,
    tableur_formule_qcm_unique: genTableurFormuleQcm,
    tableur_texte_trou_saisie: genTableurTrous,
    tableur_qcm_unique: genTableurConjectureQcm,
    etirer_texte_trou_saisie: genEtirerTrous,
    etirer_qcm_unique: genEtirerConclQcm,
    etirer_formule_qcm_unique: genEtirerFormuleQcm,
    defaut_qcm_unique: genDefautQcm,
    balayage_texte_trou_saisie: genBalayageTrous,
    balayage_qcm_unique: genBalayageQcm,
    pseudocode_texte_trou_saisie: genPseudocodeTrous,
    pseudocode_qcm_unique: genPseudocodeQcm,
    motifs_qcm_unique: genMotifsQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous',
    formule_qcm_unique: 'QCM formule', formule_texte: 'Saisie formule',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tableur', label: L_TABLEUR, formatsList: buildFormats('tableur') },
    { id: 'etirer', label: L_ETIRER, formatsList: buildFormats('etirer') },
    { id: 'defaut', label: L_DEFAUT, formatsList: buildFormats('defaut') },
    { id: 'balayage', label: L_BALAYAGE, formatsList: buildFormats('balayage') },
    { id: 'pseudocode', label: L_PSEUDOCODE, formatsList: buildFormats('pseudocode') },
    { id: 'motifs', label: L_MOTIFS, formatsList: buildFormats('motifs') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
